import React, { useState, useEffect, useId, useRef } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Loading } from '@/components/Loading';
import { RichTextEditor } from '@/components/RichTextEditor';
import { FileUpload, type UploadedFile } from '@/components/FileUpload';
import { ticketService } from '@/services/ticketService';
import { userService } from '@/services/userService';
import { attachmentService } from '@/services/attachmentService';
import { TicketPriority, TicketSource, UserRole } from '@/types/api';
import type { User, Company, CreateTicketRequest } from '@/types/api';

// Validation schema
const createTicketSchema = z.object({
  subject: z.string()
    .min(1, 'Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(200, 'Subject must be less than 200 characters'),
  description: z.string()
    .min(1, 'Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(10000, 'Description cannot exceed 10,000 characters'),
  descriptionPlainText: z.string().optional(),
  priority: z.nativeEnum(TicketPriority).optional(),
  source: z.nativeEnum(TicketSource).optional(),
  tags: z.string().optional(),
  requesterId: z.string().optional(),
  assignedToId: z.string().optional(),
  companyId: z.string().nullable().optional(),
});

type CreateTicketFormData = z.infer<typeof createTicketSchema>;

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (ticket: any) => void;
  onTicketCreated?: (ticket: any) => void;
}

/**
 * CreateTicketModal Component
 * 
 * Modal for creating new support tickets with form validation
 * and API integration.
 */
export const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onTicketCreated,
}) => {
  const { user, hasAnyRole } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [uploadConfig, setUploadConfig] = useState<any>(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
    setValue,
    trigger,
  } = useForm<CreateTicketFormData>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      subject: '',
      description: '',
      descriptionPlainText: '',
      priority: TicketPriority.Medium,
      source: TicketSource.WebForm,
      tags: '',
      requesterId: user?.role === UserRole.EndUser ? user.id : undefined,
      assignedToId: '',
      companyId: user?.companyId,
    },
  });

  // Debug log form validation errors
  React.useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Form validation errors:', errors);
    }
    console.log('Form is valid:', isValid);
  }, [errors, isValid]);

  const description = watch('description');
  const descriptionLength = description?.length || 0;

  // Load upload configuration
  useEffect(() => {
    const loadUploadConfig = async () => {
      try {
        const config = await attachmentService.getUploadConfig();
        setUploadConfig(config);
      } catch (error) {
        console.error('Failed to load upload config:', error);
        // Set minimal upload config to allow basic functionality
        setUploadConfig({
          allowedFileTypes: ['image/jpeg', 'image/png', 'application/pdf', 'text/plain'],
          maxFileSize: 10 * 1024 * 1024, // 10MB default
          maxFileSizeFormatted: '10MB',
          maxAttachments: 5
        });
      }
    };

    if (isOpen) {
      loadUploadConfig();
    }
  }, [isOpen]);

  // Handle rich text editor changes
  const handleDescriptionChange = (htmlContent: string, plainTextContent: string) => {
    setValue('description', htmlContent);
    setValue('descriptionPlainText', plainTextContent);
    trigger('description'); // Trigger validation
  };

  // Handle inline image insertion
  const handleInsertInline = (file: UploadedFile) => {
    if (!file.preview) return;
    
    // Insert image into rich text editor
    const imageHtml = `<img src="${file.preview}" alt="${file.name}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
    
    // Get current content and insert image
    const currentContent = watch('description') || '';
    const newContent = currentContent + imageHtml;
    
    handleDescriptionChange(newContent, newContent.replace(/<[^>]*>/g, ''));
  };

  // Load dropdown options for agents/admins
  useEffect(() => {
    const loadOptions = async () => {
      if (!hasAnyRole([UserRole.Admin, UserRole.Manager, UserRole.Agent]) || !isOpen) return;

      try {
        setIsLoadingOptions(true);

        // Assignment is available to all staff, while the general user directory
        // is protected by the UserManagement policy. Keep these requests separate
        // so an Agent's expected 403 on the latter cannot erase a valid agent list.
        const agentsResponse = await userService.getAllAgents();

        if (agentsResponse.success && agentsResponse.data) {
          setAgents(agentsResponse.data.data || []);
        }

        if (hasAnyRole([UserRole.Admin, UserRole.Manager])) {
          const usersResponse = await userService.getUsers({ role: [UserRole.EndUser], isActive: true });
          if (usersResponse.success && usersResponse.data) {
            setUsers(usersResponse.data.data || []);
          }
        }

        // TODO: Load companies when company service is implemented
        setCompanies([]);
        
      } catch (err) {
        console.error('Failed to load dropdown options:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, [hasAnyRole, isOpen]);

  const onSubmit = async (data: CreateTicketFormData) => {
    console.log('Form submission started with data:', data);
    
    try {
      setIsSubmitting(true);
      setError(null);

      // Process tags
      const tags = data.tags 
        ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];

      // Create ticket payload
      const ticketPayload: CreateTicketRequest = {
        subject: data.subject,
        description: data.description,
        priority: data.priority || TicketPriority.Medium,
        source: data.source || TicketSource.WebForm,
        tags,
        requesterId: data.requesterId || (user?.role === UserRole.EndUser ? user.id : undefined),
        assignedToId: data.assignedToId || undefined,
        companyId: data.companyId || user?.companyId,
        customFields: {}
      };

      console.log('Ticket payload being sent:', ticketPayload);
      console.log('Current user:', user);
      console.log('Attachments to upload:', attachments);

      // Call real API
      const response = await ticketService.createTicket(ticketPayload);
      
      console.log('API response received:', response);
      
      // Handle both ApiResponse<Ticket> format and direct Ticket format
      let ticket;
      if (response.success && response.data) {
        // Standard ApiResponse format
        console.log('Ticket created successfully (ApiResponse format):', response.data);
        ticket = response.data;
      } else if (response.id) {
        // Direct ticket format (backend returning ticket directly)
        console.log('Ticket created successfully (direct format):', response);
        ticket = response;
      } else {
        console.error('API returned failure:', response);
        throw new Error(response.message || 'Failed to create ticket');
      }

      // Upload attachments if any
      if (attachments.length > 0 && ticket.id) {
        console.log(`Uploading ${attachments.length} attachments for ticket ${ticket.id}`);
        try {
          const filesToUpload = attachments
            .filter(att => !att.error && att.file)
            .map(att => att.file);
          
          if (filesToUpload.length > 0) {
            setIsUploadingFiles(true);
            const uploadResults = await attachmentService.uploadFiles(
              filesToUpload,
              ticket.id,
              undefined,
              (fileIndex, progress) => {
                console.log(`File ${fileIndex} upload progress: ${progress}%`);
                // Update attachment progress in state
                setAttachments(prev => prev.map((att, index) => 
                  index === fileIndex 
                    ? { ...att, isUploading: true, uploadProgress: progress }
                    : att
                ));
              }
            );
            console.log('All attachments uploaded successfully:', uploadResults);
          }
        } catch (uploadError) {
          console.error('Failed to upload some attachments:', uploadError);
          // Don't fail the entire ticket creation for attachment upload errors
          setError('Ticket created successfully, but some attachments failed to upload.');
        }
      }

      setIsUploadingFiles(false);
      
      // Reset form and close modal
      reset();
      setAttachments([]);
      onClose();
      
      // Call success callback
      if (onSuccess) {
        onSuccess(ticket);
      }
      if (onTicketCreated) {
        onTicketCreated(ticket);
      }
    } catch (err) {
      console.error('Failed to create ticket:', err);
      
      // Enhanced error reporting
      if (err instanceof Error) {
        console.error('Error details:', {
          message: err.message,
          stack: err.stack
        });
      }
      
      const msg = err instanceof Error ? err.message : 'Failed to create ticket';
      setError(msg.toLowerCase().includes('failed to create ticket') ? msg : `Failed to create ticket: ${msg}`);
    } finally {
      setIsSubmitting(false);
      setIsUploadingFiles(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !isUploadingFiles) {
      reset();
      setError(null);
      setAttachments([]);
      onClose();
    }
  };

  // BugHerd #132: dialog semantics and focus management without restructuring markup.
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalA11y({ isOpen, onClose: handleClose, containerRef: dialogRef });

  if (!isOpen) return null;

  const isEndUser = user?.role === UserRole.EndUser;
  const canAssignTickets = hasAnyRole([UserRole.Admin, UserRole.Agent]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 id={titleId} className="text-lg font-semibold leading-6 text-gray-900">
              Create New Support Ticket
            </h3>
            <button
              type="button"
              aria-label="Close dialog"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={handleClose}
              disabled={isSubmitting || isUploadingFiles}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={(e) => {
            console.log('Form onSubmit triggered!', e);
            return handleSubmit(onSubmit)(e);
          }} className="space-y-6">
            {/* Error Display */}
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                Subject <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <Input
                  id="subject"
                  type="text"
                  placeholder="Brief description of your issue or request"
                  error={errors.subject?.message}
                  {...register('subject')}
                />
              </div>
            </div>

            {/* Description - Rich Text Editor */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <RichTextEditor
                  value={watch('description') || ''}
                  onChange={handleDescriptionChange}
                  placeholder="Provide detailed information about your issue...

• Describe what you were trying to do
• What happened vs. what you expected
• Any error messages you saw
• Steps to reproduce the issue"
                  disabled={isSubmitting || isUploadingFiles}
                  minHeight={150}
                  maxHeight={400}
                  showToolbar={true}
                  allowMentions={false}
                  allowTemplates={false}
                  allowFullscreen={true}
                  className={errors.description ? 'border-red-300' : ''}
                />
              </div>
              <div className="mt-1 flex justify-between">
                <div>
                  {errors.description && (
                    <p className="text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>
                <p className={`text-sm ${
                  descriptionLength > 9500 ? 'text-red-600' : 
                  descriptionLength > 9000 ? 'text-yellow-600' : 'text-gray-500'
                }`}>
                  {descriptionLength}/10000 characters
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Priority */}
              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                  Priority
                </label>
                <div className="mt-1">
                  <select
                    id="priority"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    {...register('priority')}
                  >
                    <option value={TicketPriority.Low}>Low</option>
                    <option value={TicketPriority.Medium}>Medium</option>
                    <option value={TicketPriority.High}>High</option>
                    <option value={TicketPriority.Urgent}>Urgent</option>
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                  Tags
                </label>
                <div className="mt-1">
                  <Input
                    id="tags"
                    type="text"
                    placeholder="email, mobile, billing (comma separated)"
                    {...register('tags')}
                  />
                </div>
              </div>
            </div>

            {/* Administrative Fields - Only for Agents/Admins */}
            {canAssignTickets && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Administrative Options</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Requester */}
                  <div>
                    <label htmlFor="requesterId" className="block text-sm font-medium text-gray-700">
                      Requester
                    </label>
                    <div className="mt-1">
                      <select
                        id="requesterId"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        {...register('requesterId')}
                      >
                        <option value="">Select a user (optional)</option>
                        {isLoadingOptions ? (
                          <option disabled>Loading users...</option>
                        ) : (
                          users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.fullName} ({user.email})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label htmlFor="assignedToId" className="block text-sm font-medium text-gray-700">
                      Assign to Agent
                    </label>
                    <div className="mt-1">
                      <select
                        id="assignedToId"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        {...register('assignedToId')}
                      >
                        <option value="">Unassigned</option>
                        {isLoadingOptions ? (
                          <option disabled>Loading agents...</option>
                        ) : (
                          agents.map((agent) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.fullName || `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email} ({agent.email})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
              </label>
              <FileUpload
                files={attachments}
                onFilesChange={setAttachments}
                onInsertInline={handleInsertInline}
                maxFiles={uploadConfig?.maxAttachments || 10}
                maxFileSize={uploadConfig?.maxFileSize || (25 * 1024 * 1024)}
                acceptedFileTypes={uploadConfig?.allowedFileTypes}
                disabled={isSubmitting || isUploadingFiles}
                showPreview={true}
                allowInlineImages={true}
                className=""
              />
              <p className="text-xs text-gray-500 mt-1">
                You can attach images, documents, spreadsheets, and other files to help explain your issue. 
                <span className="text-blue-600">Click the image icon to insert images directly into your description.</span>
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {isUploadingFiles ? (
                  <span className="flex items-center text-blue-600">
                    <Loading size="sm" className="mr-2" />
                    Uploading files...
                  </span>
                ) : isEndUser ? 
                  'Your ticket will be assigned to the next available agent' :
                  'You can assign this ticket now or leave it in the queue'
                }
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting || isUploadingFiles}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting || isUploadingFiles}
                  disabled={isSubmitting || isUploadingFiles}
                  onClick={(e) => {
                    console.log('Create Ticket button clicked!', e);
                    console.log('Form errors:', errors);
                    console.log('Is form valid:', isValid);
                    console.log('Attachments:', attachments);
                  }}
                >
                  {isUploadingFiles ? 'Uploading Files...' : isSubmitting ? 'Creating...' : 'Create Ticket'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
